import { exerciseKey } from '../data/staticCurriculum';
const MAX_STORED_ATTEMPTS = 50;
/** Pure learner-record contracts and untrusted-data validation. No storage or React. */
export interface StoredAttempt {
  attemptId: string;
  trackKey: string;
  exerciseSlug: string;
  wpm: number;
  accuracy: number;
  xpEarned: number;
  starsEarned: number;
  isNewPersonalBest: boolean;
  newLevel: number | null;
  nextActionType: 'NEXT_EXERCISE' | 'RETRY' | 'TRACK_COMPLETE' | 'DASHBOARD';
  nextExerciseSlug: string | null;
  elapsedSeconds: number;
  completedAt: string;
}

export interface StoredExerciseProgress {
  attemptCount: number;
  bestWpm: number | null;
  bestAccuracy: number | null;
  starsEarned: number;
  completedAt: string | null;
  lastAttemptAt: string | null;
}

export interface OpenAttempt {
  attemptId: string;
  trackKey: string;
  exerciseSlug: string;
  startedAt: string;
}

/**
 * Lifetime aggregates (GH #112), stored separately from the bounded
 * recent-history `attempts` array: trimming history never reduces earned XP,
 * completion count, or total practice time. `completedAttemptIds` makes
 * attempt completion idempotent per attempt ID (a retried submission returns
 * the original result instead of double-crediting).
 */
export interface LifetimeAggregate {
  totalXpEarned: number;
  totalCompletions: number;
  completedAttemptIds: string[];
}

/** A bounded in-progress attempt draft, restorable after a reload (GH #115). */
export interface SequenceDraft { index: number; input: string; statuses: ('pending' | 'completed' | 'skipped')[]; }

export interface StoredDraft {
  sequence?: SequenceDraft;
  attemptId: string;
  trackKey: string;
  exerciseSlug: string;
  text: string;
  elapsedSeconds: number;
  expectedFingerprint: string;
  contentRevision?: string;
  savedAt: string;
}

export interface ProgressState {
  version: 3;
  exercises: Record<string, StoredExerciseProgress>;
  attempts: StoredAttempt[];
  openAttempts: Record<string, OpenAttempt>;
  practiceDays: string[];
  totalPracticeTimeSeconds: number;
  lifetime: LifetimeAggregate;
  drafts: Record<string, StoredDraft>;
}

export const MAX_REMEMBERED_ATTEMPT_IDS = 500;
export const MAX_DRAFT_CHARACTERS = 20_000;

export function emptyState(): ProgressState {
  return {
    version: 3,
    exercises: {},
    attempts: [],
    openAttempts: {},
    practiceDays: [],
    totalPracticeTimeSeconds: 0,
    lifetime: { totalXpEarned: 0, totalCompletions: 0, completedAttemptIds: [] },
    drafts: {},
  };
}

// ---------------------------------------------------------------------------
// Storage-boundary validation (GH #111)
//
// Everything read from localStorage is untrusted: partial payloads, wrong
// types, NaN-like strings, oversized records, and future schema versions all
// arrive here. Sanitizing normalizes recoverable data and drops the rest —
// the app never throws on stored input, and a NEWER schema version is
// quarantined (backed up, left intact) rather than silently overwritten.
// ---------------------------------------------------------------------------

export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
export const NEWER_SCHEMA_BACKUP_KEY = 'typewriter_local_progress_v1.newer-schema';

export function isFiniteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

export function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && ISO_DATE_PATTERN.test(value);
}

export function isIsoTimestamp(value: unknown): value is string {
  return typeof value === 'string' && ISO_TIMESTAMP_PATTERN.test(value);
}

export function sanitizeExerciseProgress(key: string, raw: unknown): StoredExerciseProgress | null {
  if (typeof raw !== 'object' || raw === null) return null;
  if (typeof key !== 'string' || key.length === 0 || key.length > 200) return null;
  const record = raw as Record<string, unknown>;
  if (!isFiniteNonNegative(record.attemptCount) || !Number.isInteger(record.attemptCount)) {
    return null;
  }
  const bestWpm = isFiniteNonNegative(record.bestWpm) ? record.bestWpm : null;
  const bestAccuracy =
    isFiniteNonNegative(record.bestAccuracy) && record.bestAccuracy <= 1
      ? record.bestAccuracy
      : null;
  const starsEarned = isFiniteNonNegative(record.starsEarned)
    ? (Math.min(3, Math.floor(record.starsEarned)) as 0 | 1 | 2 | 3)
    : 0;
  return {
    attemptCount: record.attemptCount,
    bestWpm,
    bestAccuracy,
    starsEarned,
    completedAt: isIsoTimestamp(record.completedAt) ? record.completedAt : null,
    lastAttemptAt: isIsoTimestamp(record.lastAttemptAt) ? record.lastAttemptAt : null,
  };
}

export const NEXT_ACTION_TYPES = ['NEXT_EXERCISE', 'RETRY', 'TRACK_COMPLETE', 'DASHBOARD'] as const;

export function sanitizeAttempt(raw: unknown): StoredAttempt | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.attemptId !== 'string' || record.attemptId.length > 100) return null;
  if (typeof record.trackKey !== 'string' || typeof record.exerciseSlug !== 'string') return null;
  if (!isFiniteNonNegative(record.wpm) || !isFiniteNonNegative(record.elapsedSeconds)) return null;
  if (!isFiniteNonNegative(record.accuracy) || record.accuracy > 1) return null;
  if (!isFiniteNonNegative(record.xpEarned) || !isFiniteNonNegative(record.starsEarned)) {
    return null;
  }
  if (!isIsoTimestamp(record.completedAt)) return null;
  const nextActionType = NEXT_ACTION_TYPES.includes(
    record.nextActionType as (typeof NEXT_ACTION_TYPES)[number],
  )
    ? (record.nextActionType as StoredAttempt['nextActionType'])
    : 'DASHBOARD';
  return {
    attemptId: record.attemptId,
    trackKey: record.trackKey,
    exerciseSlug: record.exerciseSlug,
    wpm: record.wpm,
    accuracy: record.accuracy,
    xpEarned: record.xpEarned,
    starsEarned: Math.min(3, Math.floor(record.starsEarned)),
    isNewPersonalBest: record.isNewPersonalBest === true,
    newLevel:
      record.newLevel === null || isFiniteNonNegative(record.newLevel)
        ? (record.newLevel as number | null)
        : null,
    nextActionType,
    nextExerciseSlug:
      typeof record.nextExerciseSlug === 'string' ? record.nextExerciseSlug : null,
    elapsedSeconds: record.elapsedSeconds,
    completedAt: record.completedAt,
  };
}

export function sanitizeOpenAttempt(raw: unknown): OpenAttempt | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.attemptId !== 'string' || record.attemptId.length > 100) return null;
  if (typeof record.trackKey !== 'string' || typeof record.exerciseSlug !== 'string') return null;
  if (!isIsoTimestamp(record.startedAt)) return null;
  return {
    attemptId: record.attemptId,
    trackKey: record.trackKey,
    exerciseSlug: record.exerciseSlug,
    startedAt: record.startedAt,
  };
}

/** Sentinel: the payload carries a schema version newer than this code. */
export const NEWER_SCHEMA = Symbol('newer-schema');
export const CURRENT_STATE_VERSION = 3;

export function sanitizeLifetime(raw: unknown, migrated: LifetimeAggregate): LifetimeAggregate {
  if (typeof raw !== 'object' || raw === null) return migrated;
  const record = raw as Record<string, unknown>;
  const ids = Array.isArray(record.completedAttemptIds)
    ? record.completedAttemptIds.filter(
        (id): id is string => typeof id === 'string' && id.length > 0 && id.length <= 100,
      )
    : [];
  return {
    totalXpEarned: isFiniteNonNegative(record.totalXpEarned)
      ? record.totalXpEarned
      : migrated.totalXpEarned,
    totalCompletions: isFiniteNonNegative(record.totalCompletions)
      ? record.totalCompletions
      : migrated.totalCompletions,
    completedAttemptIds: ids.slice(-MAX_REMEMBERED_ATTEMPT_IDS),
  };
}

export function sanitizeProgressState(raw: unknown): ProgressState | typeof NEWER_SCHEMA {
  if (typeof raw !== 'object' || raw === null) return emptyState();
  const record = raw as Record<string, unknown>;
  if (typeof record.version === 'number' && record.version > CURRENT_STATE_VERSION) {
    return NEWER_SCHEMA;
  }

  const exercises: Record<string, StoredExerciseProgress> = {};
  if (typeof record.exercises === 'object' && record.exercises !== null) {
    for (const [key, value] of Object.entries(record.exercises)) {
      const sanitized = sanitizeExerciseProgress(key, value);
      if (sanitized) exercises[key] = sanitized;
    }
  }

  const attempts: StoredAttempt[] = Array.isArray(record.attempts)
    ? record.attempts
        .map(sanitizeAttempt)
        .filter((attempt): attempt is StoredAttempt => attempt !== null)
        .slice(0, MAX_STORED_ATTEMPTS)
    : [];

  const practiceDays = Array.isArray(record.practiceDays)
    ? Array.from(new Set(record.practiceDays.filter(isIsoDate))).sort()
    : [];

  // v1 payloads carry no lifetime aggregate: migrate best-effort from the
  // retained (possibly already-trimmed) history. Totals only ever grow from
  // here, so a v1-era trim can never reduce them again.
  const migratedLifetime: LifetimeAggregate = {
    totalXpEarned: attempts.reduce((total, attempt) => total + attempt.xpEarned, 0),
    totalCompletions: attempts.length,
    completedAttemptIds: attempts.map((attempt) => attempt.attemptId),
  };

  return {
    version: CURRENT_STATE_VERSION,
    exercises,
    attempts,
    openAttempts: sanitizeRecords(record.openAttempts, sanitizeOpenAttempt,
      sanitizeOpenAttempt(record.openAttempt), (entry) => entry.attemptId),
    practiceDays,
    totalPracticeTimeSeconds: isFiniteNonNegative(record.totalPracticeTimeSeconds)
      ? record.totalPracticeTimeSeconds
      : 0,
    lifetime: sanitizeLifetime(record.lifetime, migratedLifetime),
    drafts: sanitizeRecords(record.drafts, sanitizeDraft,
      sanitizeDraft(record.draft), (entry) => exerciseKey(entry.trackKey, entry.exerciseSlug)),
  };
}

/** Migrate single-session v1/v2 data without discarding other lesson drafts. */
export function sanitizeRecords<T>(raw: unknown, sanitize: (entry: unknown) => T | null,
  legacy: T | null, keyFor: (entry: T) => string): Record<string, T> {
  const entries: Record<string, T> = {};
  if (legacy) entries[keyFor(legacy)] = legacy;
  if (raw && typeof raw === 'object') {
    for (const value of Object.values(raw).slice(-500)) {
      const entry = sanitize(value);
      if (entry) entries[keyFor(entry)] = entry;
    }
  }
  return entries;
}

export function sanitizeDraft(raw: unknown): StoredDraft | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  if (typeof record.attemptId !== 'string' || record.attemptId.length > 100) return null;
  if (typeof record.trackKey !== 'string' || typeof record.exerciseSlug !== 'string') {
    return null;
  }
  if (typeof record.text !== 'string' || record.text.length > MAX_DRAFT_CHARACTERS) return null;
  if (!isFiniteNonNegative(record.elapsedSeconds)) return null;
  if (typeof record.expectedFingerprint !== 'string' || record.expectedFingerprint.length > 200) {
    return null;
  }
  if (!isIsoTimestamp(record.savedAt)) return null;
  return {
    attemptId: record.attemptId,
    trackKey: record.trackKey,
    exerciseSlug: record.exerciseSlug,
    text: record.text,
    elapsedSeconds: Math.floor(record.elapsedSeconds),
    expectedFingerprint: record.expectedFingerprint,
    contentRevision: typeof record.contentRevision === 'string' ? record.contentRevision.slice(0, 200) : undefined,
    savedAt: record.savedAt,
    sequence: sanitizeSequence(record.sequence),
  };
}

export function sanitizeSequence(raw: unknown): SequenceDraft | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const item = raw as Record<string, unknown>;
  if (!Number.isInteger(item.index) || Number(item.index) < 0 || Number(item.index) > 1000 ||
    typeof item.input !== 'string' || item.input.length > MAX_DRAFT_CHARACTERS || !Array.isArray(item.statuses) ||
    item.statuses.length > 1000 || !item.statuses.every(s => s === 'pending' || s === 'completed' || s === 'skipped')) return undefined;
  return { index: Number(item.index), input: item.input, statuses: item.statuses };
}

/** Cheap deterministic content fingerprint: length + rolling hash. */
export function fingerprintOf(text: string): string {
  let hash = 5381;
  for (let index = 0; index < text.length; index += 1) {
    hash = ((hash * 33) ^ text.charCodeAt(index)) >>> 0;
  }
  return `${text.length.toString(16)}-${hash.toString(16)}`;
}

